import { isIsoDay, shiftIsoDay } from '../isoDay.ts';

export interface ChipOption<TId extends string> {
    id: TId;
    label: string;
}

interface DayPreset<TId extends string> extends ChipOption<TId> {
    days?: number;
}

export type DayFormatter = (isoDay: string) => string;

export type LastScanPreset = 'any' | '7d' | '30d' | '90d' | 'since';

export const LAST_SCAN_OPTIONS: readonly DayPreset<LastScanPreset>[] = [
    { id: 'any', label: 'Any time' },
    { id: '7d', label: 'Last 7 days', days: 7 },
    { id: '30d', label: 'Last 30 days', days: 30 },
    { id: '90d', label: 'Last 90 days', days: 90 },
    { id: 'since', label: 'Since a date' }
];

export const DEFAULT_LAST_SCAN_PRESET: LastScanPreset = 'any';

export type ScannedPreset = '7d' | '30d' | '90d' | 'custom';

export const SCANNED_OPTIONS: readonly DayPreset<ScannedPreset>[] = [
    { id: '7d', label: 'Last 7 days', days: 7 },
    { id: '30d', label: 'Last 30 days', days: 30 },
    { id: '90d', label: 'Last 90 days', days: 90 },
    { id: 'custom', label: 'Custom range' }
];

export const DEFAULT_SCANNED_PRESET: ScannedPreset = '30d';

export const DATE_RANGE_ERROR = 'The From date must be on or before the To date.';

function presetDays<TId extends string>(
    options: readonly DayPreset<TId>[],
    id: TId
): number | undefined {
    return options.find((option) => option.id === id)?.days;
}

function presetLabel<TId extends string>(options: readonly DayPreset<TId>[], id: TId): string {
    return options.find((option) => option.id === id)?.label ?? '';
}

export function lastScanDateFrom(
    preset: LastScanPreset,
    since: string,
    today: string
): string | undefined {
    if (preset === 'since') return isIsoDay(since) ? since : undefined;
    const days = presetDays(LAST_SCAN_OPTIONS, preset);
    return days === undefined ? undefined : shiftIsoDay(today, -days);
}

export function lastScanChipValue(
    preset: LastScanPreset,
    since: string,
    formatDay: DayFormatter
): string {
    if (preset !== 'since') return presetLabel(LAST_SCAN_OPTIONS, preset);
    return isIsoDay(since) ? `Since ${formatDay(since)}` : 'Since a date';
}

export interface CustomRange {
    from: string;
    to: string;
}

export interface ScanDateRange {
    dateFrom?: string;
    dateTo?: string;
}

// A blank side of a custom range sends no bound, matching an open-ended search.
export function scannedRange(
    preset: ScannedPreset,
    custom: CustomRange,
    today: string
): ScanDateRange {
    if (preset === 'custom') {
        return {
            dateFrom: isIsoDay(custom.from) ? custom.from : undefined,
            dateTo: isIsoDay(custom.to) ? custom.to : undefined
        };
    }
    const days = presetDays(SCANNED_OPTIONS, preset) ?? 0;
    return { dateFrom: shiftIsoDay(today, -days), dateTo: today };
}

export function customRangeError(preset: ScannedPreset, custom: CustomRange): string | null {
    if (preset !== 'custom' || !isIsoDay(custom.from) || !isIsoDay(custom.to)) return null;
    return custom.from > custom.to ? DATE_RANGE_ERROR : null;
}

// Switching to a custom range starts from the range already on screen, not from blank inputs.
export function seedCustomRange(
    preset: ScannedPreset,
    custom: CustomRange,
    today: string
): CustomRange {
    if (custom.from || custom.to) return custom;
    const { dateFrom = '', dateTo = '' } = scannedRange(preset, custom, today);
    return { from: dateFrom, to: dateTo };
}

export function scannedChipValue(
    preset: ScannedPreset,
    custom: CustomRange,
    formatDay: DayFormatter
): string {
    if (preset !== 'custom') return presetLabel(SCANNED_OPTIONS, preset);
    const from = isIsoDay(custom.from) ? formatDay(custom.from) : null;
    const to = isIsoDay(custom.to) ? formatDay(custom.to) : null;
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Until ${to}`;
    return 'Custom range';
}
